import React, { useState } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Dialog } from 'primereact/dialog'
import { FaRegTrashAlt } from 'react-icons/fa'
import {
  Btn,
  BtnIcon,
  CustomDropdown,
  CustomRadioButton,
  InputNumeroDecimal,
  InputText,
  SectionTitle,
} from '../../../../../components'
import {
  IBS_CBS_SITUACOES_TRIBUTARIAS,
  IBS_CBS_CLASSIFICACOES_POR_SITUACAO,
} from '../../../../../constants/ibs-cbs-tributacao'
import { ICMS_SITUACOES_TRIBUTARIAS } from '../../../../../constants/icms-tributacao'
import {
  aplicarCampoFiscal,
  aplicarTotal,
  calcularIcmsPorDentro,
  novoComponente,
  round2,
  somaComponentes,
  toNum,
} from './valoresFiscaisCte.util'

// ICMS: 'composto' = frete já embute o ICMS | 'calcular' = calcular por dentro e somar ao frete
const ICMS_MODOS = [
  { value: 'composto', label: 'Valor do Frete já compõe ICMS' },
  { value: 'calcular', label: 'Calcular ICMS e somar ao valor do Frete' },
]

const porValor = (opcoes, valor) =>
  opcoes.find(opcao => String(opcao.value) === String(valor)) || null

// Componentes do Valor, valores do frete, ICMS e IBS/CBS da emissão de CTe. O modal guarda os
// campos fiscais em `state` e a lista `componentes`; aqui ficam a interface e os cálculos.
export default function ValoresFiscaisCte({
  state,
  onStateChange,
  componentes,
  onComponentesChange,
  onError,
  componentesEmptyMessage = 'Nenhum componente. Os valores do frete podem ser digitados diretamente nos campos abaixo.',
  componentesHint = 'Nome do componente: máximo de 15 caracteres (limite da SEFAZ).',
  componentesActions = null,
}) {
  const [icmsModo, setIcmsModo] = useState('composto')
  const [icmsCalc, setIcmsCalc] = useState({ valor_frete: null, aliquota: null })
  const [deleteDialog, setDeleteDialog] = useState({ visible: false, item: null })

  const icmsSituacao = porValor(ICMS_SITUACOES_TRIBUTARIAS, state.icms_situacao_tributaria)
  const ibsCbsSituacao = porValor(IBS_CBS_SITUACOES_TRIBUTARIAS, state.ibs_cbs_situacao_tributaria)
  const ibsCbsClassificacoes = ibsCbsSituacao
    ? IBS_CBS_CLASSIFICACOES_POR_SITUACAO[ibsCbsSituacao.value] || []
    : []
  const ibsCbsClassificacao = porValor(ibsCbsClassificacoes, state.ibs_cbs_classificacao_tributaria)

  const alterarCampo = (campo, value) =>
    onStateChange(prevState => aplicarCampoFiscal(prevState, campo, value))

  // Componentes do Valor
  const onComponenteChange = (item, campo, valor) => {
    const novos = componentes.map(c => (c.key === item.key ? { ...c, [campo]: valor } : c))
    onComponentesChange(novos)
    if (campo === 'valor')
      onStateChange(prevState => aplicarTotal(prevState, somaComponentes(novos)))
  }

  const onNovoCampo = () => onComponentesChange([...componentes, novoComponente()])

  const removerComponente = deduzir => {
    const novos = componentes.filter(c => c.key !== deleteDialog.item.key)
    onComponentesChange(novos)
    if (deduzir) onStateChange(prevState => aplicarTotal(prevState, somaComponentes(novos)))
    setDeleteDialog({ visible: false, item: null })
  }

  // Alerta de divergência entre a soma dos componentes e os valores do frete
  const soma = somaComponentes(componentes)
  const somaDiverge =
    componentes.length > 0 &&
    (toNum(state.valor_total) !== soma || toNum(state.valor_receber) !== soma)

  // ICMS calculado por dentro
  const icmsCalculado = calcularIcmsPorDentro(icmsCalc.valor_frete, icmsCalc.aliquota)

  const onIcmsModoChange = modo => {
    if (modo === 'calcular') {
      setIcmsCalc({ valor_frete: state.valor_total, aliquota: state.icms_aliquota })
    }
    setIcmsModo(modo)
  }

  const onAdicionarIcmsClicked = () => {
    try {
      if (!icmsCalculado.valido) {
        throw new Error(
          'Informe o Valor Frete e uma Alíquota ICMS entre 0 e 100 para calcular o ICMS',
        )
      }
      const novos = [...componentes, novoComponente('ICMS', icmsCalculado.valor)]
      const total = componentes.length
        ? somaComponentes(novos)
        : round2(toNum(icmsCalc.valor_frete) + icmsCalculado.valor)
      onComponentesChange(novos)
      onStateChange(prevState => ({
        ...aplicarTotal(prevState, total),
        icms_aliquota: icmsCalc.aliquota,
        icms_valor: icmsCalculado.valor,
      }))
      setIcmsModo('composto')
    } catch (error) {
      onError(error)
    }
  }

  const renderDecimal = (campo, label) => (
    <div className="p-md-6 p-col-12">
      <InputNumeroDecimal
        label={label}
        marginTop={10}
        value={state[campo]}
        onChange={value => alterarCampo(campo, value)}
      />
    </div>
  )

  const renderSituacaoIcms = () => (
    <div className="p-md-6 p-col-12">
      <CustomDropdown
        optionLabel="label"
        options={ICMS_SITUACOES_TRIBUTARIAS}
        value={icmsSituacao}
        label="Situação Tributária ICMS:*"
        filter={true}
        onChange={value => alterarCampo('icms_situacao_tributaria', value.value)}
      />
    </div>
  )

  return (
    <>
      {/* Componentes do Valor da Prestação */}
      <div className="p-col-12">
        <SectionTitle title="Componentes do Valor" />
        <div style={{ display: 'flex', gap: 10, marginTop: 5, marginBottom: 5 }}>
          <Btn className="p-button-success" onClick={onNovoCampo}>
            Novo Campo
          </Btn>
          {componentesActions}
        </div>
        <DataTable value={componentes} emptyMessage={componentesEmptyMessage}>
          <Column
            field="nome"
            header="Nome"
            body={item => (
              <InputText
                marginTop={0}
                showLabel={false}
                value={item.nome || ''}
                onChange={value => onComponenteChange(item, 'nome', value.slice(0, 15))}
              />
            )}
          />
          <Column
            field="valor"
            header="Valor"
            style={{ width: 180 }}
            body={item => (
              <InputNumeroDecimal
                marginTop={0}
                showLabel={false}
                value={item.valor}
                onChange={value => onComponenteChange(item, 'valor', value)}
              />
            )}
          />
          <Column
            header=""
            style={{ width: 60, textAlign: 'center' }}
            body={item => (
              <BtnIcon onClick={() => setDeleteDialog({ visible: true, item })}>
                <FaRegTrashAlt color="red" size={18} />
              </BtnIcon>
            )}
          />
        </DataTable>
        <span style={{ color: 'gray', fontSize: 12 }}>{componentesHint}</span>
      </div>

      {renderDecimal('valor_total', 'Valor Bruto Frete:*')}
      {renderDecimal('valor_receber', 'Valor Líquido Frete:*')}
      {somaDiverge && (
        <div className="p-col-12" style={{ paddingTop: 0 }}>
          <div style={{ color: '#e67e22', fontWeight: 'bold' }}>
            Atenção! A soma dos componentes não resulta no Valor Total do Frete
          </div>
          <div style={{ color: '#e67e22', fontSize: 12 }}>
            * Apenas um alerta! Não impede a emissão do CTe.
          </div>
        </div>
      )}

      {/* ICMS */}
      <div className="p-col-12">
        <SectionTitle title="ICMS" />
        <div style={{ marginTop: 5 }}>
          <CustomRadioButton items={ICMS_MODOS} value={icmsModo} onChange={onIcmsModoChange} />
        </div>
      </div>
      {icmsModo === 'composto' ? (
        <>
          {renderSituacaoIcms()}
          {renderDecimal('icms_base_calculo', 'Base de cálculo ICMS:*')}
          {renderDecimal('icms_aliquota', 'Alíquota ICMS:*')}
          {renderDecimal('icms_valor', 'Valor ICMS:*')}
        </>
      ) : (
        <>
          <div className="p-md-6 p-col-12">
            <InputNumeroDecimal
              label="Valor Frete:"
              marginTop={10}
              value={icmsCalc.valor_frete}
              onChange={value => setIcmsCalc({ ...icmsCalc, valor_frete: value })}
            />
          </div>
          {renderSituacaoIcms()}
          <div className="p-md-6 p-col-12">
            <InputNumeroDecimal
              label="Alíquota ICMS:"
              marginTop={10}
              value={icmsCalc.aliquota}
              onChange={value => setIcmsCalc({ ...icmsCalc, aliquota: value })}
            />
          </div>
          <div className="p-md-6 p-col-12" />
          <div className="p-md-6 p-col-12">
            <InputNumeroDecimal
              label="Valor Bruto Frete/ Base de cálculo ICMS (após cálculo):"
              marginTop={10}
              value={icmsCalculado.base}
              onChange={() => {}}
              disabled={true}
            />
          </div>
          <div className="p-md-6 p-col-12">
            <InputNumeroDecimal
              label="Valor ICMS:"
              marginTop={10}
              value={icmsCalculado.valor}
              onChange={() => {}}
              disabled={true}
            />
          </div>
          <div className="p-col-12" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '0.9rem' }}>
              Acrescentar Valor ICMS e Atualizar Valor de Frete
            </span>
            <Btn className="p-button-success" onClick={onAdicionarIcmsClicked}>
              Adicionar
            </Btn>
          </div>
        </>
      )}

      {/* IBS/CBS */}
      <div className="p-col-12">
        <SectionTitle title="IBS/CBS" />
      </div>
      <div className="p-md-6 p-col-12">
        <CustomDropdown
          optionLabel="label"
          options={IBS_CBS_SITUACOES_TRIBUTARIAS}
          value={ibsCbsSituacao}
          label="Situação Tributária IBS/CBS:*"
          filter={true}
          onChange={value => alterarCampo('ibs_cbs_situacao_tributaria', value.value)}
        />
      </div>
      <div className="p-md-6 p-col-12">
        <CustomDropdown
          optionLabel="label"
          options={ibsCbsClassificacoes}
          value={ibsCbsClassificacao}
          label="Classificação Tributária IBS/CBS:*"
          filter={true}
          disabled={!state.ibs_cbs_situacao_tributaria}
          onChange={value => alterarCampo('ibs_cbs_classificacao_tributaria', value.value)}
        />
      </div>
      {renderDecimal('ibs_cbs_base_calculo', 'Base de cálculo IBS/CBS:*')}
      {renderDecimal('ibs_uf_aliquota', 'Alíquota IBS - UF:*')}
      {renderDecimal('ibs_uf_valor', 'Valor IBS - UF:*')}
      {renderDecimal('ibs_mun_aliquota', 'Alíquota IBS - Município:*')}
      {renderDecimal('ibs_mun_valor', 'Valor IBS - Município:*')}
      {renderDecimal('ibs_valor_total', 'Valor IBS - Total:*')}
      {renderDecimal('cbs_aliquota', 'Alíquota CBS:*')}
      {renderDecimal('cbs_valor', 'Valor CBS:*')}

      {/* Confirmação de exclusão de componente */}
      <Dialog
        header="Excluir Componente"
        visible={deleteDialog.visible}
        modal={true}
        onHide={() => setDeleteDialog({ visible: false, item: null })}
      >
        <div style={{ marginTop: 10, marginBottom: 10 }}>Deduzir Valor do Total do Frete?</div>
        <div className="p-grid">
          <div className="p-col-6 text-center">
            <Btn onClick={() => removerComponente(true)}>Deduzir</Btn>
          </div>
          <div className="p-col-6 text-center">
            <Btn onClick={() => removerComponente(false)}>Manter</Btn>
          </div>
        </div>
      </Dialog>
    </>
  )
}
